<script setup lang="ts">
import { computed, useId } from 'vue';
import { ChartData, Chart as ChartJS, ChartOptions, registerables } from 'chart.js';
import { IDate, IYearMonth } from '@/coreDomainsModule/date';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { i18n } from '@/i18n';
import { Kpi } from '@/inventoryDashboardModule/domains/entities/Kpi';
import { Line } from 'vue-chartjs';
import { SummaryAmountsByDimension } from '@/inventoryDashboardModule/domains/entities/Dashboard';

const t = i18n.t;

const props = defineProps<{
  summaryAmounts: SummaryAmountsByDimension[];
  kpiData: Kpi[];
  fromYearMonth: IYearMonth;
  toYearMonth: IYearMonth;
  useAdjusted?: boolean;
  minScale?: number;
  maxScale?: number;
}>();

const chartId = useId();
ChartJS.register(...registerables);

const periodLength = computed(() => DateService.calculateIYearMonthsDiff(props.fromYearMonth, props.toYearMonth, true));

const startMonthDiff = computed(() =>
  DateService.calculateIYearMonthsDiff(props.fromYearMonth, DateService.getShiftedIYearMonthFromNowDateByMonths(0))
);

const kpiGraphData = computed(() => {
  const res: { x: number; y: number; meta: { date: number } }[] = [];
  let kpiIndex = 0;
  for (let i = -1; i < periodLength.value + 1; i++) {
    if (!props.kpiData[kpiIndex]) break;
    const t: IDate = {
      ...DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + i + 1),
      day: 1,
    };
    const nowKpi = props.kpiData[kpiIndex];
    if (!nowKpi.valid_to || DateService.compareDates(nowKpi.valid_to, t) !== -1) {
      // その月を満たす形でそのKPIがある場合、次の月の月初に点を打つ
      res.push({ x: i, y: props.kpiData[kpiIndex].kpiValue, meta: { date: 0 } });
    } else {
      // その月の途中でそのKPIが終了する場合、終了時点に点を打つ
      res.push({ x: --i + nowKpi.valid_to.day / 30, y: props.kpiData[kpiIndex + 1]?.kpiValue ?? 0, meta: { date: nowKpi.valid_to.day } });
      kpiIndex++;
    }
  }
  return res;
});

const kpiAchievementData = computed(() => {
  const res: { x: number; y: number; meta: { date: number } }[] = [];
  for (let i = 0; i < periodLength.value + 1; i++) {
    const t: IDate = {
      ...DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + i),
      day: 1,
    };
    const targetKpiArchvementData = props.summaryAmounts.find((a) => DateService.compareYearMonths(a.yearMonth, t) === 0);
    if (props.useAdjusted) {
      if (!targetKpiArchvementData || !targetKpiArchvementData.turnoverWithForecastAccuracy) continue;
      res.push({ x: i, y: targetKpiArchvementData.turnoverWithForecastAccuracy, meta: { date: 0 } });
      continue;
    }
    if (!targetKpiArchvementData || !targetKpiArchvementData.turnover) continue;
    res.push({ x: i, y: targetKpiArchvementData.turnover, meta: { date: 0 } });
  }
  return res;
});

const chartData = computed(
  (): ChartData<'line', { x: number; y: number; meta: { date: number } }[]> => ({
    datasets: [
      {
        label: t('実績'),
        data: kpiAchievementData.value,
        borderColor: '#1e92ef',
        backgroundColor: '#1e92ef',
        segment: {
          // eslint-disable-next-line jsdoc/require-jsdoc
          borderDash: (ctx) => {
            const x = ctx.p0.parsed.x;

            if (x >= Math.abs(startMonthDiff.value) - 1) {
              return [4, 4];
            }
            return undefined;
          },
        },
      },
      {
        label: t('目標'),
        parsing: { xAxisKey: 'x', yAxisKey: 'y' },
        data: kpiGraphData.value,
        stepped: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        borderColor: '#2e7d32',
        backgroundColor: '#2e7d32',
      },
    ],
  })
);

const chartOptions = computed<ChartOptions<'line'>>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  parsing: false,
  scales: {
    x: {
      type: 'linear',
      min: 0,
      max: periodLength.value,
      ticks: {
        stepSize: 1,
        // eslint-disable-next-line jsdoc/require-jsdoc
        callback: (value) => {
          const v = Number(value);
          if (!Number.isInteger(v)) return '';
          return DateService.convertIYearMonthToYYYYMMWithSlash(
            DateService.getShiftedIYearMonthFromNowDateByMonths(startMonthDiff.value + v)
          );
        },
      },
    },
    y: {
      min: props.minScale,
      max: props.maxScale,
    },
  },
  interaction: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  hover: {
    mode: 'nearest',
    axis: 'x',
    intersect: false,
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
      callbacks: {
        /**
         * ツールチップの数値を小数1桁目まで確実に表示する
         * @param ctx - Tooltipのobj
         */
        title: (ctx) => {
          const date = DateService.getShiftedDateFromNowDateByMonths(startMonthDiff.value + ctx[0].parsed.x + 1);
          date.setDate((ctx[0].raw as { x: number; y: number; meta: { date: number } }).meta.date);
          return DateService.convertIYearMonthDayToYYYYMMDDWithi18n(DateService.convertDateToIDate(date));
        },
        /**
         * ツールチップの数値を小数1桁目まで確実に表示する
         * @param ctx - Tooltipのobj
         */
        label: (ctx) => {
          const v = ctx.parsed.y;
          if (v == null) return '';
          const label =
            ctx.dataset.label === t('実績') ? (ctx.parsed.x >= Math.abs(startMonthDiff.value) ? t('予測') : t('実績')) : ctx.dataset.label;
          return `${label}: ${v.toFixed(1)}`;
        },
      },
    },
  },
}));
</script>
<template>
  <Line :id="chartId" :data="chartData" :options="chartOptions" />
</template>
<style lang="scss" scoped></style>
